import React, {Suspense, useEffect} from 'react';
import {BrowserRouter as Router, Routes, Route, useLocation, useNavigate} from 'react-router-dom';

import ShopLayout from './Layouts/Shop';

import { ThemeProvider, createTheme } from '@mui/material/styles';
import { isIOS, isBrowser } from 'react-device-detect';
import {CssBaseline, Typography} from "@mui/material";
import {useWebSocket} from "./context/WebSocketContext";
import NotificationWithTimer from "./Components/Notification/NotificationWithTimer";
import {useSelector} from "react-redux";
import {useTranslation} from "react-i18next";
import {applyTelegramFullscreenMode} from "./utils/telegramFullscreenMode";
import {resolveTelegramTheme} from "./utils/telegramTheme";

const PolicyLayout = React.lazy(() => import('./Layouts/PolicyLayout'));
const Adminpanel = React.lazy(() => import('./Layouts/Adminpanel'));

const RouteFallback = () => <div style={{minHeight: "100vh"}} />;

const App = () => {
    const WebApp = window.Telegram.WebApp;

    useEffect(() => {
        WebApp.enableClosingConfirmation();
        applyTelegramFullscreenMode(WebApp);
    }, [WebApp]);

    return (
        <Router>
            <AnimatedRoutes />
        </Router>
    );
};

const getTheme = (platform, paddingTop) => {

    var telegramTheme = resolveTelegramTheme(
        window.Telegram.WebApp.themeParams,
        window.Telegram.WebApp.colorScheme
    );


    window.Telegram.WebApp.onEvent('themeChanged', () => {
        telegramTheme = resolveTelegramTheme(
            window.Telegram.WebApp.themeParams,
            window.Telegram.WebApp.colorScheme
        );
    });

    if(isBrowser || (isIOS && window.Telegram.WebApp.colorScheme === 'light')) {
        [telegramTheme.secondary_bg_color, telegramTheme.bg_color] = [
            telegramTheme.bg_color,
            telegramTheme.secondary_bg_color
        ];

        window.Telegram.WebApp.setHeaderColor('secondary_bg_color');
    }

    return createTheme({
        typography: {
            fontFamily: isIOS
                ?
                '-apple-system, BlinkMacSystemFont, "San Francisco", "Helvetica Neue", sans-serif'
                :
                'Roboto, Arial, sans-serif',
        },
        components: {
            MuiCssBaseline: {
                styleOverrides: {
                    '#root': {
                        backgroundColor: telegramTheme.bg_color,
                        color: telegramTheme.text_color,
                    },
                    'body': {
                        backgroundColor: telegramTheme.bg_color,
                        color: telegramTheme.text_color
                    },
                    '.paddingTopSafeArea': {
                        paddingTop: paddingTop
                    },
                    '.blackBlock': {
                        width: "100%",
                        position: "fixed",
                        zIndex: 20000,
                        height: paddingTop,
                        background: telegramTheme.bg_color,
                        top: 0
                    }
                },
            },
            MuiButtonBase: {
                defaultProps: {
                    disableRipple: isIOS
                },
                styleOverrides: {
                    root: {
                        borderRadius: isIOS ? '12px' : '4px',
                    },
                },
            },
            MuiButton: {
                defaultProps: {
                    disableRipple: isIOS
                },
                styleOverrides: {
                    root: {
                        borderRadius: isIOS ? '12px' : '4px',
                    },
                },
            },
            MuiListItemButton: {
                styleOverrides: {
                    root: {
                        '&:hover': {
                            backgroundColor: isIOS ? 'rgba(0, 0, 0, 0.04)' : 'rgba(0, 0, 0, 0.04)'
                        },
                        display: 'flex',
                        padding: isIOS ? '8px' : '12px',
                        paddingLeft: '12px',
                        paddingRight: "12px",
                        fontSize: "16px",
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        borderRadius: isIOS ? '12px' : '0'
                    },
                },
            },
            MuiListItem: {

            },
            MuiCard: {
                styleOverrides: {
                    root: {
                        borderRadius: isIOS ? '12px' : '0',
                        padding: '12px',
                        background: telegramTheme.secondary_bg_color,
                        boxShadow: "none",
                        border: "0"
                    },
                },
            },
            MuiStack: {
                styleOverrides: {
                    root: {
                        padding: isIOS ? '12px' : '0',
                        background: telegramTheme.bg_color,
                        boxShadow: "none",
                        border: "0"
                    }
                }
            },
            MuiSkeleton: {
                styleOverrides: {
                    root: {
                        backgroundColor: telegramTheme.secondary_bg_color,
                        borderRadius: isIOS ? "12px" : "0px",
                        '&::after': {
                            background: telegramTheme.text_color,
                        },
                    }
                }
            },
            MuiList: {
                styleOverrides: {
                    root: {
                        borderRadius: isIOS ? '12px' : '0',
                        padding: isIOS ? '4px 0px' : '0',
                        background: telegramTheme.secondary_bg_color
                    },
                },
            },
            MuiDivider: {
                styleOverrides: {
                    root: {
                        background: telegramTheme.subtitle_text_color,
                        height: "1px",
                        border: 'none'
                    },
                },
            },
            MuiDialogContent: {
                styleOverrides: {
                    root: {
                        background: telegramTheme.bg_color,
                        padding: "0px",
                    },
                },
            },
            MuiDialog: {
                styleOverrides: {
                    root: {
                        paddingTop: paddingTop
                    }
                }
            },
            MuiDialogTitle: {
                styleOverrides: {
                    root: {
                        background: telegramTheme.bg_color,
                        padding: "0px"
                    },
                },
            },
            MuiDialogActions: {
                styleOverrides: {
                    root: {
                        background: telegramTheme.bg_color,
                        padding: "0px"
                    },
                },
            },
            MuiTypography: {
                defaultProps: {
                    variantMapping: {
                        h1: 'h1',
                        h2: 'h2',
                        h3: 'h3',
                        h4: 'h4',
                        h5: 'h5',
                        h6: 'h6',
                        small: 'small'
                    },
                },
                styleOverrides: {
                    root: {
                        color: telegramTheme.text_color
                    }
                }
            },
            MuiTab: {
                styleOverrides: {
                    root: {
                        color: telegramTheme.text_color
                    }
                }
            },
            MuiMenu: {
                styleOverrides: {
                    root: {
                        color: telegramTheme.bg_color
                    }
                }
            },
            MuiMenuList: {
                styleOverrides: {
                    root: {
                        color: telegramTheme.bg_color
                    }
                }
            },
            MuiTextField: {
                styleOverrides: {
                    root: {
                        color: telegramTheme.text_color,
                        borderRadius: isIOS ? "12px" : "0",
                        background: telegramTheme.secondary_bg_color
                    }
                }
            },
            MuiSnackbarContent: {
                styleOverrides: {
                    root: {
                        borderRadius: isIOS ? "12px" : "4px",
                        padding: "4px 12px"
                    }
                }
            },
            MuiInput: {
                styleOverrides: {
                    root: {
                        color: telegramTheme.text_color
                    }
                }
            },
            MuiInputBase: {
                styleOverrides: {
                    root: {
                        color: telegramTheme.text_color
                    }
                }
            },
            MuiInputLabel: {
                styleOverrides: {
                    root: {
                        color: telegramTheme.text_color,
                    }
                }
            },
            MuiDialogContentText: {
                styleOverrides: {
                    root: {
                        color: telegramTheme.text_color
                    }
                }
            }
        },
        palette: {
            bg_color: telegramTheme.bg_color,
            secondary_bg_color: telegramTheme.secondary_bg_color,
            text_color: telegramTheme.text_color,
            link_color: telegramTheme.link_color,
            hint_color: telegramTheme.hint_color,
            subtitle_text_color: telegramTheme.subtitle_text_color
        },
    });
};

const AnimatedRoutes = () => {
    const location = useLocation();

    const shopSettings = useSelector(state => state.shop.shop.settings);
    const userSession = useSelector(state => state.user.user);

    const[showPayNotification, setShowPayNotification] = React.useState(false);

    const {t} = useTranslation();

    const platform = isIOS ? 'ios' : '';

    const [paddingTop, setPaddingTop] = React.useState(0);

    React.useEffect(() => {
        const handleSafeAreaChange = () => {
            const safeAreaTop = window.Telegram.WebApp.safeAreaInset !== undefined ? window.Telegram.WebApp.safeAreaInset.top * 2 : 0;
            setPaddingTop(safeAreaTop);
        };

        window.Telegram.WebApp.onEvent('safeAreaChanged', handleSafeAreaChange);

        return () => {
            window.Telegram.WebApp.offEvent('safeAreaChanged', handleSafeAreaChange);
        };
    }, []);

    const theme = getTheme(platform, paddingTop);

    const webSocketManager = useWebSocket();

    const [openNotification, setOpenNotification] = React.useState(false);
    const [notificationText, setNotificationText] = React.useState(false);
    const [notificationButtonText, setNotificationButtonText] = React.useState(false);
    const [notificationAction, setNotificationAction] = React.useState(() => () => {
        setOpenNotification(false);
    });

    useEffect(() => {
        const handler = (data) => {
            const message = data.message

            setNotificationButtonText(t('close'));

            if(message.action !== undefined) {
                if(message.action === 'update') {
                    setNotificationAction(() => () => {
                        window.location.href = message.link;
                    });
                    setNotificationButtonText(t('reload'));
                }
            }

            setNotificationText(t(`${message.text}`, {param: message.param}));
            setOpenNotification(true);
        }

        webSocketManager.addHandler("notify_user", handler);

        return () => {
            webSocketManager.removeHandler("notify_user");
        }

    }, [setOpenNotification]);

    useEffect(() => {
        if(shopSettings !== undefined && shopSettings.sub !== undefined) {
            if(shopSettings.sub.hasInactiveSub === true && shopSettings.sub.is_infinity === 0 && shopSettings.countGoods > 50) {
                setShowPayNotification(true);
            }
        }
    }, [shopSettings]);

    const navigate = useNavigate();

    return (
        <ThemeProvider theme={theme}>
            <div className={"paddingTopSafeArea"}  style={{background: theme.palette.bg_color}}>
                <div className="blackBlock"></div>
                <NotificationWithTimer
                    open={openNotification}
                    onClose={notificationAction}
                    message={
                        <div className={"d-flex justify-content-between align-items-center w-100"}>
                            <Typography>{notificationText}</Typography>
                            <Typography onClick={notificationAction} sx={{ color: "link_color" }}>{notificationButtonText}</Typography>
                        </div>
                    }
                    position="bottom"
                    time={5}
                />
                <NotificationWithTimer
                    open={showPayNotification}
                    onClose={() => {}}
                    message={
                        <div className={"d-flex justify-content-between align-items-center w-100"}>
                            <Typography>{t('pay_notification')}</Typography>
                            <Typography sx={{ color: "link_color" }} onClick={() => {
                                navigate(`/adminpanel/${userSession.shopid}/tariffs`)
                            }}>{t('pay')}</Typography>
                        </div>
                    }
                    position="top"
                    time={0}
                />
                <CssBaseline/>
                    <Routes location={location} key={location.pathname}>
                        {typeof (window.Telegram.WebApp.initDataUnsafe.user) !== 'undefined' ? (
                            <>
                                <Route
                                    path="/"
                                    element={
                                        <div key="welcome">
                                            <p>Welcome</p>
                                        </div>
                                    }
                                />
                                <Route
                                    path="/temp_shop"
                                    element={
                                        <div key="temp_shop">
                                            <ShopLayout view={"shop"}/>
                                        </div>
                                    }
                                />
                                <Route
                                    path="/shop/:shopID"
                                    element={
                                        <div key="shop">
                                            <ShopLayout view={"shop"}/>
                                        </div>
                                    }
                                />
                                <Route
                                    path="/shop/:shopID/policy/:policy"
                                    element={
                                        <div key="policy">
                                            <Suspense fallback={<RouteFallback />}>
                                                <PolicyLayout/>
                                            </Suspense>
                                        </div>
                                    }
                                />
                                <Route
                                    path="/shop/:shopID/account"
                                    element={
                                        <div key="account">
                                            <ShopLayout view={"account"}/>
                                        </div>
                                    }
                                />
                                <Route
                                    path="/adminpanel/:shopID/:page/:param"
                                    element={
                                        <div key="adminpanel_param">
                                            <Suspense fallback={<RouteFallback />}>
                                                <Adminpanel/>
                                            </Suspense>
                                        </div>
                                    }
                                />
                                <Route
                                    path="/adminpanel/:shopID/:page"
                                    element={
                                        <div key="adminpanel">
                                            <Suspense fallback={<RouteFallback />}>
                                                <Adminpanel/>
                                            </Suspense>
                                        </div>
                                    }
                                />
                            </>
                        ) : (
                            <>
                                <Route
                                    path="/"
                                    element={
                                        <div key="block">
                                            <p>block</p>
                                        </div>
                                    }
                                />
                                <Route
                                    path="/shop/:shopID"
                                    element={
                                        <div key="shop_block">
                                            <p>block</p>
                                        </div>
                                    }
                                />
                            </>
                        )}
                    </Routes>
            </div>
        </ThemeProvider>
);
};

export default App;
